import { useMutation, useQueryClient } from "@tanstack/react-query";
import { post, del } from "@/lib/api";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { FileOutput, Trash2 } from "lucide-react";
import { toast } from "sonner";

// Convert-to-PDF + Delete actions for a single DMS file row. Reused across every document
// list in the app (Employee Directory, My Documents, generic DMS browser, PM project
// documents) so the behavior and permissions are consistent everywhere, not reimplemented
// per page. Wrap the row in className="group" for the hover-reveal styling to apply.
export function DmsFileActions({ file, queryKey, canConvert, canDelete }) {
  const qc = useQueryClient();

  const convert = useMutation({
    mutationFn: () => post(`/dms/files/${file.id}/convert-to-pdf`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); toast.success("Converted to PDF"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not convert to PDF"),
  });
  const remove = useMutation({
    mutationFn: () => del(`/dms/files/${file.id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); toast.success("Document deleted"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not delete document"),
  });

  const showConvert = canConvert && file.type !== "pdf";
  if (!showConvert && !canDelete) return null;

  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {showConvert && (
        <button type="button" title="Convert to PDF" data-testid={`convert-pdf-${file.id}`} disabled={convert.isPending}
          onClick={() => convert.mutate()}
          className="rounded p-1 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-foreground disabled:opacity-50">
          <FileOutput className="h-4 w-4" />
        </button>
      )}
      {canDelete && (
        <ConfirmDelete testid={`del-doc-${file.id}`} title={`Delete ${file.name}?`}
          trigger={<button type="button" title="Delete" className="rounded p-1 text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
          onConfirm={() => remove.mutate()} />
      )}
    </div>
  );
}
